import React, { useEffect, useMemo, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Decision, getProjectDecisions, createDecision } from '../../../services/decisionService';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import BoardModal, {
  BMCancelButton, BMPrimaryButton, BM_LABEL, BM_INPUT, BM_TEXTAREA, BM_TITLE_INPUT,
} from '../../../components/BoardModal';
import TagSection from '../../../components/TagSection';
import { TagPills, TagMultiFilter } from '../../../components/TagPills';
import { Tag, TagLink, DECISION_TAG_POOL, getProjectTagLinks, attachTags } from '../../../services/tagService';

interface Props { pca: IPublicClientApplication; projectId: string; }

export default function DecisionsTab({ pca, projectId }: Props) {
  const [items, setItems] = useState<Decision[]>([]);
  const [showLog, setShowLog] = useState(false);
  const [selected, setSelected] = useState<Decision | null>(null);
  const [tagLinks, setTagLinks] = useState<Map<string, TagLink[]>>(new Map());
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const me = useCurrentUser(pca, projectId);

  // Tags load is best-effort — a failure must not take down the log.
  const reloadTags = () =>
    getProjectTagLinks(pca, DECISION_TAG_POOL, projectId)
      .then(setTagLinks)
      .catch(() => setTagLinks(new Map()));
  const reload = () => { getProjectDecisions(pca, projectId).then(setItems); reloadTags(); };
  useEffect(() => { reload(); }, [pca, projectId]); // eslint-disable-line

  // Unique tags in use across this project's decisions — the filter options.
  const tagOptions = useMemo(() => {
    const byId = new Map<string, Tag>();
    tagLinks.forEach(links => links.forEach(l => byId.set(l.tag.id, l.tag)));
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [tagLinks]);

  // OR logic: a decision must carry at least one of the selected tags.
  const visible = tagFilter.length
    ? items.filter(d => {
        const ids = (tagLinks.get(d.dagrun_pm_decisionid) ?? []).map(l => l.tag.id);
        return tagFilter.some(id => ids.includes(id));
      })
    : items;

  return (
    <div style={{ padding: '20px 32px', fontFamily: 'var(--font-ui)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 12 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#0F1117' }}>Decisions</h3>
        {/* Tag filter sits to the LEFT of the Log Decision button, same
            row, matched heights. Dropdown anchors to its right edge so it
            never overflows the viewport. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <TagMultiFilter
            options={tagOptions}
            selected={tagFilter}
            onChange={setTagFilter}
            align="right"
            triggerStyle={{ height: 34, padding: '0 12px', display: 'inline-flex', alignItems: 'center' }}
          />
          <button type="button" onClick={() => setShowLog(true)} disabled={!me}
            style={{ background: '#0E7490', color: '#fff', border: 'none', height: 34, padding: '0 16px', borderRadius: 6, fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: me ? 1 : 0.5, display: 'inline-flex', alignItems: 'center' }}
            onMouseEnter={e => me && (e.currentTarget.style.background = '#0A5568')}
            onMouseLeave={e => (e.currentTarget.style.background = '#0E7490')}
          >+ Log Decision</button>
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 13, color: '#9198A8' }}>
          Decisions are immutable once logged.
        </div>
      </div>

      {items.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: '#9198A8', fontSize: 14 }}>No decisions logged yet.</div>
      ) : visible.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: '#9198A8', fontSize: 14 }}>No decisions match the selected tags.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ borderBottom: '1px solid #DDE0E6', textAlign: 'left' }}>
            <Th>Title</Th><Th>Decision Date</Th><Th>Made By</Th><Th>Tags</Th><Th>Logged</Th>
          </tr></thead>
          <tbody>
            {visible.map(d => (
              <tr key={d.dagrun_pm_decisionid} onClick={() => setSelected(d)}
                style={{ borderBottom: '0.5px solid #EAECF0', cursor: 'pointer', transition: 'background 0.12s' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#F5F6F8')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}>
                <Td><span style={{ color: '#0F1117', fontSize: 14 }}>{d.dagrun_pm_name}</span></Td>
                <Td><span style={{ fontSize: 12, color: '#5A6272', fontFamily: 'var(--font-mono)' }}>{d.dagrun_pm_decisiondate ? new Date(d.dagrun_pm_decisiondate).toLocaleDateString() : '—'}</span></Td>
                <Td><span style={{ fontSize: 13, color: '#5A6272' }}>{d.dagrun_pm_madebyname ?? '—'}</span></Td>
                <Td><TagPills tags={(tagLinks.get(d.dagrun_pm_decisionid) ?? []).map(l => l.tag)} /></Td>
                <Td><span style={{ fontSize: 12, color: '#9198A8', fontFamily: 'var(--font-mono)' }}>{d.createdon ? new Date(d.createdon).toLocaleDateString() : '—'}</span></Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showLog && me && (
        <LogDecisionModal pca={pca} projectId={projectId} meOid={me.oid} meName={me.displayName}
          onClose={() => setShowLog(false)}
          onCreated={() => { setShowLog(false); reload(); }}
        />
      )}
      {selected && <DecisionPanel pca={pca} projectId={projectId} decision={selected} onTagsChanged={reloadTags} onClose={() => setSelected(null)} />}
    </div>
  );
}

const Th = ({ children }: { children: React.ReactNode }) => (
  <th style={{ padding: '8px 12px', fontWeight: 500, fontSize: 12, color: '#5A6272', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{children}</th>
);
const Td = ({ children }: { children: React.ReactNode }) => (
  <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>{children}</td>
);

function LogDecisionModal({ pca, projectId, meOid, meName, onClose, onCreated }: { pca: IPublicClientApplication; projectId: string; meOid: string; meName: string; onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [decisionText, setDecisionText] = useState('');
  const [rationale, setRationale] = useState('');
  const [impact, setImpact] = useState('');
  const [decisiondate, setDecisiondate] = useState(() => new Date().toISOString().slice(0, 10));
  const [stagedTags, setStagedTags] = useState<Tag[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim() || !rationale.trim() || saving) return;
    setSaving(true); setError(null);
    try {
      const id = await createDecision(pca, projectId, {
        name: name.trim(),
        decisiontext: decisionText.trim() || null,
        rationale: rationale.trim(),
        impact: impact.trim() || null,
        decisiondate: decisiondate || null,
        madeby: meOid,
        madebyname: meName,
      });
      // Commit staged tags — junctions were deferred until the decision had
      // an ID. On createDecision failure we never reach this line, so the
      // staged selection is discarded with no orphaned junction records.
      await attachTags(pca, DECISION_TAG_POOL, id, name.trim(), stagedTags);
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <BoardModal open title="Log Decision" onClose={onClose}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={saving} />
        <BMPrimaryButton onClick={submit} disabled={saving || !name.trim() || !rationale.trim()}>{saving ? 'Logging…' : 'Log Decision'}</BMPrimaryButton>
      </>}
    >
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Title *</label>
        <input autoFocus value={name} onChange={e => setName(e.target.value)} style={BM_TITLE_INPUT} placeholder="Short, specific decision title" />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Decision</label>
        <textarea value={decisionText} onChange={e => setDecisionText(e.target.value)} style={BM_TEXTAREA} placeholder="What was decided? The decision statement itself." />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Rationale *</label>
        <textarea value={rationale} onChange={e => setRationale(e.target.value)} style={BM_TEXTAREA} placeholder="Why was this decision made?" />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Impact</label>
        <textarea value={impact} onChange={e => setImpact(e.target.value)} style={BM_TEXTAREA} placeholder="Optional — what does this affect?" />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Decision Date</label>
        <input type="date" value={decisiondate} onChange={e => setDecisiondate(e.target.value)} style={BM_INPUT} />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Tags</label>
        <TagSection
          pca={pca}
          pool={DECISION_TAG_POOL}
          projectId={projectId}
          stagedTags={stagedTags}
          onStagedChange={setStagedTags}
        />
      </div>
      <div style={{ fontSize: 12, color: '#9198A8' }}>
        Logged by <strong style={{ color: '#5A6272' }}>{meName}</strong>. Decisions cannot be edited after logging.
      </div>
      {error && <div style={{ marginTop: 12, color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}

function DecisionPanel({ pca, projectId, decision, onTagsChanged, onClose }: { pca: IPublicClientApplication; projectId: string; decision: Decision; onTagsChanged?: () => void; onClose: () => void }) {
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,17,23,0.18)', zIndex: 100 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, height: '100%', width: 480,
        background: '#fff', borderLeft: '0.5px solid #EAECF0',
        boxShadow: '0 4px 24px rgba(15,17,23,0.12)', zIndex: 101,
        overflowY: 'auto', padding: '16px 24px 32px 24px',
        fontFamily: 'var(--font-ui)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
          <button onClick={onClose} aria-label="Close" style={{ width: 28, height: 28, border: 'none', background: 'transparent', cursor: 'pointer', color: '#5A6272', fontSize: 18 }}>×</button>
        </div>

        <div style={{ fontSize: 18, fontWeight: 600, color: '#0F1117', marginBottom: 4 }}>{decision.dagrun_pm_name}</div>
        <div style={{ fontSize: 12, color: '#9198A8', marginBottom: 18 }}>Read-only — decisions are immutable.</div>

        <Eyebrow>Decision Date</Eyebrow>
        <Body>{decision.dagrun_pm_decisiondate ? new Date(decision.dagrun_pm_decisiondate).toLocaleDateString() : '—'}</Body>

        <Eyebrow>Made By</Eyebrow>
        <Body>{decision.dagrun_pm_madebyname ?? '—'}</Body>

        {/* What was decided → why → what it affects */}
        <Eyebrow>Decision</Eyebrow>
        <Body>{decision.dagrun_pm_decisiontext ?? '—'}</Body>

        <Eyebrow>Rationale</Eyebrow>
        <Body>{decision.dagrun_pm_rationale ?? '—'}</Body>

        <Eyebrow>Impact</Eyebrow>
        <Body>{decision.dagrun_pm_impact ?? '—'}</Body>

        <Eyebrow>Logged</Eyebrow>
        <Body>{decision.createdon ? new Date(decision.createdon).toLocaleString() : '—'}</Body>

        {/* Tags are junction metadata — editable even though the decision
            record itself is immutable. */}
        <Eyebrow>Tags</Eyebrow>
        <div style={{ marginTop: 8 }}>
          <TagSection
            pca={pca}
            pool={DECISION_TAG_POOL}
            entityId={decision.dagrun_pm_decisionid}
            entityName={decision.dagrun_pm_name}
            projectId={projectId}
            onChanged={onTagsChanged}
          />
        </div>
      </div>
    </>
  );
}

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <div style={{ fontSize: 11, fontWeight: 600, color: '#5A6272', textTransform: 'uppercase', letterSpacing: '0.12em', marginTop: 16, marginBottom: 4, paddingTop: 12, borderTop: '0.5px solid #EAECF0' }}>{children}</div>
);
const Body = ({ children }: { children: React.ReactNode }) => (
  <div style={{ fontSize: 14, color: '#0F1117', lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{children}</div>
);
